import type { AspectHit, ChartPointId, WesternAspectType } from '@astrally/shared';
import { InfoDot } from '../../components/common';

/**
 * The Western reading of the same pair.
 *
 * Synastry has no total. It is not a scored system and never was — it lays two
 * charts over each other and describes what the contact between them is like.
 * Rendering it as a percentage would be inventing a number the tradition does
 * not produce, so this component deliberately shows none, including the
 * `score` that `computeSynastry` returns for the matching engine's internal
 * use. Every line below names the exact aspect and orb it came from, so the
 * prose is always traceable back to engine output.
 */

const POINT_LABEL: Partial<Record<ChartPointId, string>> = {
  sun: 'Sun',
  moon: 'Moon',
  mercury: 'Mercury',
  venus: 'Venus',
  mars: 'Mars',
  jupiter: 'Jupiter',
  saturn: 'Saturn',
  uranus: 'Uranus',
  neptune: 'Neptune',
  pluto: 'Pluto',
  ascendant: 'Ascendant',
  mc: 'Midheaven',
  fortune: 'Part of Fortune',
};

const label = (p: ChartPointId): string => POINT_LABEL[p] ?? String(p);

const ASPECT_VERB: Record<WesternAspectType, string> = {
  conjunction: 'conjunct',
  opposition: 'opposite',
  trine: 'trine',
  square: 'square',
  sextile: 'sextile',
  quincunx: 'quincunx',
  'semi-sextile': 'semi-sextile',
  'semi-square': 'semi-square',
  sesquiquadrate: 'sesquiquadrate',
};

/** Four behavioural families. The narrative copy is keyed on these, not on all nine angles. */
type Family = 'fusing' | 'flowing' | 'tense' | 'adjusting';

const FAMILY: Record<WesternAspectType, Family> = {
  conjunction: 'fusing',
  trine: 'flowing',
  sextile: 'flowing',
  opposition: 'tense',
  square: 'tense',
  'semi-square': 'tense',
  sesquiquadrate: 'tense',
  quincunx: 'adjusting',
  'semi-sextile': 'adjusting',
};

type ChapterId = 'luminaries' | 'attraction' | 'mercury' | 'saturn';

interface Chapter {
  id: ChapterId;
  title: string;
  term: string;
  /** What this chapter of a synastry reading is for. */
  premise: string;
  /** Said when the two charts make no contact at all in this chapter. */
  silence: string;
}

const CHAPTERS: Chapter[] = [
  {
    id: 'luminaries',
    title: 'Sun and Moon',
    term: 'moon-sign',
    premise:
      'The Sun is the self you are consciously building; the Moon is what you do without deciding to. Contacts between them describe how immediately two people recognise each other, and how safe that recognition feels.',
    silence:
      'Neither Sun nor Moon touches the other chart within orb. That is not a negative — it means the immediate, gut-level recognition this pairing is usually asked about simply is not the channel these two charts run on.',
  },
  {
    id: 'attraction',
    title: 'Venus and Mars',
    term: 'aspect',
    premise:
      'Venus is what you find beautiful and how you show affection; Mars is how you pursue and how you fight. This pair is where Western astrology locates attraction, and it says nothing about whether attraction is a good idea.',
    silence:
      'No Venus or Mars contact inside orb. Charts without it are not sexless charts — attraction has other routes, this one just is not lit up.',
  },
  {
    id: 'mercury',
    title: 'Mercury — how you talk',
    term: 'aspect',
    premise:
      'Mercury is the wiring of thought and speech: pace, register, whether you get to the point or arrive at it sideways. It is the least romantic factor in synastry and often the one that decides whether people can stay in a room together.',
    silence:
      'No Mercury contact within orb. Communication is then something you build by practice rather than something the charts hand you.',
  },
  {
    id: 'saturn',
    title: 'Saturn — weight and commitment',
    term: 'saturn-return',
    premise:
      'Saturn is limit, structure and time. Saturn contacts are what make a relationship durable, and they are also where it gets heavy. Traditional Western practice treats them as the difference between a connection and a commitment.',
    silence:
      'No Saturn contact within orb. Nothing structural is being imposed by either chart on the other, for better and for worse.',
  },
];

/**
 * Authored prose per chapter and family. The exact planets are named in the
 * provenance line above each paragraph, so the paragraph itself can stay about
 * what the contact MEANS rather than restating the arithmetic.
 */
const NARRATIVE: Record<ChapterId, Record<Family, string>> = {
  luminaries: {
    fusing:
      'One person’s core sense of self sits right on top of the other’s emotional reflexes. It usually reads as instant familiarity — the feeling of having met before. The cost of that closeness is perspective: it can be hard to tell where one reaction ends and the other begins.',
    flowing:
      'Identity and feeling point in the same direction here. Being around each other tends to be low-effort, and easy contacts ask nothing of either of you. Enjoy it as comfort rather than reading it as depth.',
    tense:
      'What one of you needs in order to feel secure sits close to what the other needs in order to feel like themselves. This is the long-burn contact — high engagement, real friction, and no version of it that quietly resolves on its own. Traditionally it is read as the reason people stay interested, not as a reason to leave.',
    adjusting:
      'Self and feeling are wired just differently enough that you keep having to translate. Neither reading of a situation is the wrong one; both have to be said out loud rather than assumed.',
  },
  attraction: {
    fusing:
      'Taste and drive land on the same degree. Attraction here tends to be immediate and physical, and it tends to be the first thing either of you notices. Immediate is not the same as durable — this contact says nothing about the second year.',
    flowing:
      'Wanting and being wanted move in the same direction. Pursuit feels uncomplicated rather than fraught, which is pleasant and, in the classical reading, slightly unmemorable.',
    tense:
      'Attraction with a charge in it. What one of you finds appealing sits close to what the other finds provoking, so the pull and the argument come from the same place. This is the contact most often mistaken for chemistry and most often mistaken for a problem; it is genuinely both.',
    adjusting:
      'Desire and affection are angled awkwardly to each other — close enough to matter, not close enough to be automatic. It reads as constant small recalibration about timing and pace.',
  },
  mercury: {
    fusing:
      'You think in the same register. Sentences get finished, shorthand develops fast, and the risk is an echo chamber rather than a conversation.',
    flowing:
      'Conversation runs without translation. Disagreements stay about the topic rather than sliding into how the topic was raised.',
    tense:
      'You process at different speeds, or at different altitudes. One of you will experience the other as either too blunt or too roundabout, and the argument tends to become about the way it was said rather than what was said.',
    adjusting:
      'The wires nearly meet. You will often be answering slightly different questions and only notice several exchanges later.',
  },
  saturn: {
    fusing:
      'Saturn sits directly on a personal point in the other chart. This is the contact classical Western astrology associates with staying — and with the sense of being measured. It gives a relationship a spine and it gives it a ceiling, and which of those it feels like usually depends on how the Saturn person handles their own limits.',
    flowing:
      'Saturn steadies rather than restricts here. It reads as the reliable version of commitment: structure that is available if you want it rather than imposed on you.',
    tense:
      'Saturn on a personal point at a hard angle is the weight in this pairing. It is read as duty, delay and the feeling of being held to a standard. Traditional practice regards it as the thing that makes a relationship serious; it is also the thing that makes it tiring.',
    adjusting:
      'Saturn touches obliquely. Responsibility keeps getting redistributed rather than settling — it is less a burden than an unresolved question about who is carrying what.',
  },
};

/** Which chapter a hit belongs to. Each hit is used exactly once, in this order. */
function chapterOf(hit: AspectHit): ChapterId | null {
  const pair = new Set<ChartPointId>([hit.a, hit.b]);
  const only = (...ids: ChartPointId[]) => [...pair].every((p) => ids.includes(p));
  if (only('sun', 'moon')) return 'luminaries';
  if (only('venus', 'mars')) return 'attraction';
  if (pair.has('mercury')) return 'mercury';
  if (pair.has('saturn')) return 'saturn';
  return null;
}

export interface HouseOverlay {
  /** Whose planet it is is implied by which list this sits in. */
  planet: ChartPointId;
  /** 1-12 in the other person's chart. */
  house: number;
}

export interface SynastryViewProps {
  /** From `computeSynastry(selfPositions, partnerPositions)` — `a` is yours, `b` is theirs. */
  aspects: AspectHit[];
  selfLabel: string;
  partnerLabel: string;
  /** Only computable when both birth times are exact enough to fix the houses. */
  overlays?: {
    partnerInSelf: HouseOverlay[];
    selfInPartner: HouseOverlay[];
  };
  /** Set when a birth time is approximate — the honest caveat about the fast points. */
  caveat?: string;
  /** How many contacts to narrate per chapter before summarising the rest. */
  perChapter?: number;
}

/** What area of life each house stands for, in plain language. */
const HOUSE_THEME: string[] = [
  'how you show up and are first read',
  'money, security and what you think is worth keeping',
  'everyday talk, siblings and the short journeys',
  'home, family and the private self',
  'play, romance and whatever you make for its own sake',
  'routine, work and the body',
  'partnership and the one-to-one',
  'shared resources, intimacy and what is not said in public',
  'belief, distance and the bigger picture',
  'career, reputation and the public record',
  'friends, networks and the future being aimed at',
  'solitude, the unconscious and what is kept back',
];

function OverlayList({
  title,
  items,
  lead,
}: {
  title: string;
  items: HouseOverlay[];
  lead: string;
}) {
  if (items.length === 0) return null;
  return (
    <div className="col" style={{ gap: 6 }}>
      <div className="small" style={{ color: 'var(--mode-ink)', fontWeight: 600 }}>
        {title}
      </div>
      <div className="tiny muted" style={{ lineHeight: 1.55 }}>
        {lead}
      </div>
      {items.map((o) => (
        <div key={`${o.planet}-${o.house}`} className="tiny" style={{ lineHeight: 1.55 }}>
          <span style={{ color: 'var(--mode-ink)', fontWeight: 600 }}>{label(o.planet)}</span> in
          house {o.house} — <span className="muted">{HOUSE_THEME[o.house - 1] ?? 'that area of life'}</span>
        </div>
      ))}
    </div>
  );
}

export function SynastryView({
  aspects,
  selfLabel,
  partnerLabel,
  overlays,
  caveat,
  perChapter = 3,
}: SynastryViewProps) {
  // Group once; tightest orb first, because a 0.4° contact is doing far more
  // work than a 5.8° one and the reader should meet it first.
  const grouped = new Map<ChapterId, AspectHit[]>();
  for (const hit of [...aspects].sort((x, y) => x.orb - y.orb)) {
    const id = chapterOf(hit);
    if (!id) continue;
    const list = grouped.get(id) ?? [];
    list.push(hit);
    grouped.set(id, list);
  }

  const counted = [...grouped.values()].reduce((s, l) => s + l.length, 0);

  return (
    <section data-mode="western" className="col" style={{ gap: 10 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="mode-chip">Western · tropical</span>
        <span className="tiny faint">No score</span>
      </div>
      <div className="mode-rule" />

      <div
        className="card col"
        style={{ gap: 8, background: 'var(--mode-soft)', borderColor: 'var(--mode-line)' }}
      >
        <div className="row" style={{ alignItems: 'baseline', gap: 6 }}>
          <span
            style={{ fontSize: '2.4rem', fontWeight: 700, color: 'var(--mode-ink)', lineHeight: 1 }}
          >
            {counted}
          </span>
          <span className="muted">contacts read below</span>
          <InfoDot term="synastry" />
        </div>
        <div className="tiny muted" style={{ lineHeight: 1.6 }}>
          Synastry does not produce a total, and this screen will not invent one. A count of
          contacts is a measure of how much these two charts have to say to each other, not of how
          well it goes. Every line names its aspect and its <InfoDot term="orb" /> orb so you can
          judge how exact it actually is.
        </div>
        {caveat && (
          <div className="banner banner--gold small" role="status">
            {caveat}
          </div>
        )}
      </div>

      {CHAPTERS.map((chapter) => {
        const hits = grouped.get(chapter.id) ?? [];
        const shown = hits.slice(0, perChapter);
        const rest = hits.length - shown.length;
        return (
          <div key={chapter.id} className="card col" style={{ gap: 8 }}>
            <div className="row" style={{ gap: 2, alignItems: 'center' }}>
              <span className="h3">{chapter.title}</span>
              <InfoDot term={chapter.term} />
            </div>
            <div className="tiny muted" style={{ lineHeight: 1.6 }}>
              {chapter.premise}
            </div>

            {shown.length === 0 ? (
              <div className="tiny faint" style={{ lineHeight: 1.6, marginTop: 2 }}>
                {chapter.silence}
              </div>
            ) : (
              shown.map((hit) => (
                <div
                  key={`${hit.a}-${hit.type}-${hit.b}-${hit.orb}`}
                  style={{
                    borderLeft: '2px solid var(--mode-line)',
                    paddingLeft: 10,
                    marginTop: 2,
                  }}
                >
                  {/* Provenance: the engine's own row, before any interpretation of it. */}
                  <div className="tiny" style={{ color: 'var(--mode-ink)', fontWeight: 600 }}>
                    {selfLabel}&rsquo;s {label(hit.a)} {ASPECT_VERB[hit.type]} {partnerLabel}
                    &rsquo;s {label(hit.b)}
                    <InfoDot term={hit.type} />
                    <span className="faint" style={{ fontWeight: 400 }}>
                      {' '}
                      orb {hit.orb.toFixed(1)}&deg;
                    </span>
                  </div>
                  <div className="small muted" style={{ marginTop: 4, lineHeight: 1.6 }}>
                    {NARRATIVE[chapter.id][FAMILY[hit.type]]}
                  </div>
                </div>
              ))
            )}

            {rest > 0 && (
              <div className="tiny faint">
                {rest} further contact{rest === 1 ? '' : 's'} in this area, at wider orbs. Wider
                means weaker, not absent.
              </div>
            )}
          </div>
        );
      })}

      {/* --- house overlays ---------------------------------------------- */}
      <div className="card col" style={{ gap: 10 }}>
        <div className="row" style={{ gap: 2, alignItems: 'center' }}>
          <span className="h3">House overlays</span>
          <InfoDot term="house-systems" />
        </div>
        {overlays ? (
          <>
            <div className="tiny muted" style={{ lineHeight: 1.6 }}>
              Aspects describe the quality of a contact; overlays describe where it lands. Dropping
              one person&rsquo;s planets into the other&rsquo;s houses shows which department of
              life each of you activates in the other — often the most practically useful part of a
              synastry reading, and the part that needs both birth times to be accurate.
            </div>
            <OverlayList
              title={`${partnerLabel} in ${selfLabel}’s chart`}
              items={overlays.partnerInSelf}
              lead={`Where ${partnerLabel} shows up in your life.`}
            />
            <OverlayList
              title={`${selfLabel} in ${partnerLabel}’s chart`}
              items={overlays.selfInPartner}
              lead={`Where you show up in ${partnerLabel}’s life.`}
            />
          </>
        ) : (
          <div className="tiny muted" style={{ lineHeight: 1.6 }}>
            Overlays need both birth times to be exact, because houses are set by the rising sign
            and that moves roughly one degree every four minutes. With an approximate time the
            houses would be a guess dressed up as a reading, so they are left out rather than
            estimated.
          </div>
        )}
      </div>

      <div className="tiny faint" style={{ lineHeight: 1.6 }}>
        Synastry describes a dynamic between two charts. It is an interpretive language for talking
        about how people affect each other, not evidence about what either person is like or a
        forecast of what happens next. Tense contacts describe engagement, not damage; easy ones
        describe comfort, not virtue.
      </div>
    </section>
  );
}
